<template>
  <div class="usage-dashboard">
    <div class="usage-dashboard__header">
      <div class="usage-dashboard__title-group">
        <h1 class="usage-dashboard__title">School Metrics</h1>
        <span
          v-if="organizationName"
          class="usage-dashboard__org-name paragraph-s"
          >{{ organizationName }}</span
        >
      </div>
      <eg-button
        type="secondary"
        icon="download"
        icon-size="medium"
        :is-loading="isExportingCsv"
        @click="onExportCsv"
      >
        Download CSV
      </eg-button>
    </div>

    <div class="usage-dashboard__content">
      <usage-dashboard-filters
        :courses="courses"
        :instructors="instructors"
        :course-id="courseId"
        :instructor-id="instructorId"
        :date-range="dateRange"
        @update:courseId="onCourseChange"
        @update:instructorId="onInstructorChange"
        @update:dateRange="onDateRangeChange"
        @reset="onResetFilters"
      />

      <div
        class="usage-dashboard__summary"
        :class="{ 'usage-dashboard__summary--loading': isSummaryLoading }"
      >
        <metrics-summary-card
          icon="done-check-document-file"
          label="Submissions graded"
          :value="summary.submissionsGraded"
        />
        <metrics-summary-card
          icon="medal-prize-reward"
          label="Average grade"
          :display-value="averageGradeLabel"
        />
        <metrics-summary-card
          icon="graduation-cap-classes"
          label="Courses in view"
          :value="summary.coursesInView"
        />
        <metrics-summary-card
          icon="users-group"
          label="Instructors in view"
          :value="summary.instructorsInView"
        />
        <metrics-summary-card
          icon="clock"
          label="Time saved"
          :display-value="timeSavedLabel"
          highlighted
        />
        <div v-if="isSummaryLoading" class="usage-dashboard__summary-spinner" />
      </div>

      <usage-dashboard-chart
        :labels="chart.labels"
        :dataset="chart.datasets[0] || { label: '', data: [] }"
        :is-loading="isChartLoading"
        :metric="chartMetric"
        :group-by="chartGroupBy"
        @update:metric="onChartMetricChange"
        @update:groupBy="onChartGroupByChange"
      />

      <usage-dashboard-table
        :org-id="orgId"
        :course-id="courseId"
        :instructor-id="instructorId"
        :date-from="apiDateFrom"
        :date-to="apiDateTo"
        @query-change="onTableQueryChange"
      />
    </div>
  </div>
</template>

<script>
import UsageDashboardFilters from "./components/usage_dashboard_filters.vue";
import UsageDashboardChart from "./components/usage_dashboard_chart.vue";
import UsageDashboardTable from "./components/usage_dashboard_table.vue";
import MetricsSummaryCard from "@/views/metrics/components/overview/metrics_summary_card.vue";
import {
  getUsageDashboardFilters,
  getUsageDashboardSummary,
  getUsageDashboardChart,
  exportUsageDashboardCourses,
} from "@/api/requests/usageDashboard.js";

function toISODate(ms) {
  if (!ms) return undefined;
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default {
  name: "UsageDashboardView",

  components: {
    UsageDashboardFilters,
    UsageDashboardChart,
    UsageDashboardTable,
    MetricsSummaryCard,
  },

  data() {
    return {
      organizationName: "",
      courses: [],
      instructors: [],
      courseId: null,
      instructorId: null,
      dateRange: { from: "", to: "" },
      summary: {
        submissionsGraded: 0,
        averageGrade: null,
        timeSavedHours: 0,
        timeSavedPercent: 0,
        coursesInView: 0,
        instructorsInView: 0,
      },
      chart: { labels: [], instructorIds: null, datasets: [] },
      chartMetric: "submissions",
      chartGroupBy: "total",
      isSummaryLoading: false,
      isChartLoading: false,
      isExportingCsv: false,
      tableQuery: { search: undefined, sortBy: undefined, sortDir: undefined },
    };
  },

  computed: {
    orgId() {
      return this.$route.params.orgId;
    },

    apiDateFrom() {
      return toISODate(this.dateRange.from);
    },

    apiDateTo() {
      return toISODate(this.dateRange.to);
    },

    filtersKey() {
      return [
        this.courseId,
        this.instructorId,
        this.apiDateFrom,
        this.apiDateTo,
      ].join("_");
    },

    averageGradeLabel() {
      return this.summary.averageGrade != null
        ? `${this.summary.averageGrade}%`
        : "—";
    },

    timeSavedLabel() {
      return `${this.summary.timeSavedHours.toLocaleString()} h`;
    },
  },

  watch: {
    filtersKey() {
      this.fetchSummary();
      this.fetchChart();
    },

    chartMetric() {
      this.fetchChart();
    },

    chartGroupBy() {
      this.fetchChart();
    },
  },

  created() {
    this.fetchFilters();
    this.fetchSummary();
    this.fetchChart();
  },

  methods: {
    async fetchFilters() {
      try {
        const res = await getUsageDashboardFilters(this.orgId);
        this.organizationName = res.data?.organizationName || "";
        this.courses = res.data?.courses || [];
        this.instructors = res.data?.instructors || [];
      } catch {
        this.courses = [];
        this.instructors = [];
      }
    },

    async fetchSummary() {
      this.isSummaryLoading = true;
      try {
        const res = await getUsageDashboardSummary({
          orgId: this.orgId,
          courseId: this.courseId,
          instructorId: this.instructorId,
          dateFrom: this.apiDateFrom,
          dateTo: this.apiDateTo,
        });
        this.summary = { ...this.summary, ...res.data };
      } catch {
        // keep last known summary on failure
      } finally {
        this.isSummaryLoading = false;
      }
    },

    async fetchChart() {
      this.isChartLoading = true;
      try {
        const res = await getUsageDashboardChart({
          orgId: this.orgId,
          courseId: this.courseId,
          instructorId: this.instructorId,
          dateFrom: this.apiDateFrom,
          dateTo: this.apiDateTo,
          metric: this.chartMetric,
          groupBy: this.chartGroupBy,
        });
        this.chart = res.data || {
          labels: [],
          instructorIds: null,
          datasets: [],
        };
      } catch {
        this.chart = { labels: [], instructorIds: null, datasets: [] };
      } finally {
        this.isChartLoading = false;
      }
    },

    onCourseChange(value) {
      this.courseId = value;
    },

    onInstructorChange(value) {
      this.instructorId = value;
    },

    onDateRangeChange(value) {
      this.dateRange = value;
    },

    onResetFilters() {
      this.courseId = null;
      this.instructorId = null;
      this.dateRange = { from: "", to: "" };
    },

    onChartMetricChange(value) {
      this.chartMetric = value;
    },

    onChartGroupByChange(value) {
      this.chartGroupBy = value;
    },

    onTableQueryChange(query) {
      this.tableQuery = query;
    },

    async onExportCsv() {
      if (this.isExportingCsv) return;
      this.isExportingCsv = true;
      try {
        const { data, headers } = await exportUsageDashboardCourses({
          orgId: this.orgId,
          courseId: this.courseId,
          instructorId: this.instructorId,
          dateFrom: this.apiDateFrom,
          dateTo: this.apiDateTo,
          search: this.tableQuery.search,
          sortBy: this.tableQuery.sortBy,
          sortDir: this.tableQuery.sortDir,
        });
        const contentDisposition = headers?.["content-disposition"];
        const fileName =
          contentDisposition?.match(/filename="(.+)"/)?.[1] ||
          "school-metrics-usage.csv";

        const url = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$showToastError?.(
          error?.response?.data?.error || "Export failed. Please try again."
        );
      } finally {
        this.isExportingCsv = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../metrics/components/shared/metrics_card";

.usage-dashboard {
  @include metrics-view;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px 24px;
    background: var(--color-surface-neutral-background, #fff);
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  }

  &__title-group {
    display: flex;
    align-items: baseline;
    gap: 12px;
  }

  &__title {
    color: var(--color-text-neutral-on-white, #1a2330);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: 24px;
    font-weight: var(--typography-font-weight-fw-700, 700);
    line-height: 120%;
    margin: 0;
    white-space: nowrap;
  }

  &__org-name {
    color: var(--color-text-neutral-fade);
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;

    @media (max-width: 1024px) {
      gap: 16px;
    }
  }

  &__summary {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    transition: opacity 0.15s ease;

    @media (max-width: 1024px) {
      gap: 12px;
    }

    &--loading {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__summary-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 22px;
    margin: -11px 0 0 -11px;
    border: 2.5px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-top-color: var(--color-surface-primary-default, #8a3ffc);
    border-radius: 50%;
    animation: usage-dashboard-spin 0.8s linear infinite;
  }
}

@keyframes usage-dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
